import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiPost, apiGet, apiPatch } from 'api-service';
import { apiRemove } from 'api-service/index';
import { supportInitialState } from './supportInitialState';

export const fetchTickets = createAsyncThunk(
  'supportReducer/fetchTickets',
  async (params = {}, thunkAPI) => {
    const { settingsReducer } = thunkAPI.getState();
    try {
      const response = await apiGet('/search/api/v1/ticket', {
        page: 1,
        per_page:
          settingsReducer?.filters?.public?.pageContentCountSlider
            ?.selectedValue,
        ...params,
      });

      return {
        data: response.data?.result?.data,
        total: response.data?.result?.meta?.total,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchSupportCategories = createAsyncThunk(
  'supportReducer/fetchSupportCategories',
  async (_, thunkAPI) => {
    try {
      const response = await apiGet('/search/api/v1/ticket-category');

      return {
        data: response.data?.result?.data,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteSupportCategories = createAsyncThunk(
  'supportReducer/deleteSupportCategories',
  async (params = {}, thunkAPI) => {
    const { supportReducer } = thunkAPI.getState();
    try {
      await apiRemove(
        '/search/api/v1/ticket-category/:ids'.replace(
          ':ids',
          params?.ids?.join(',')
        )
      );

      let _temp = [...supportReducer.categories.data];
      params?.ids?.forEach((id) => {
        _temp = _temp?.filter((category) => category?.id !== id);
      });

      return {
        data: _temp,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchTicket = createAsyncThunk(
  'supportReducer/fetchTicket',
  async (params = {}, thunkAPI) => {
    try {
      const response = await apiGet(
        '/search/api/v1/ticket/:id'.replace(':id', params?.id),
        {}
      );

      return {
        data: response.data?.result?.data,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const closeTicketRequest = createAsyncThunk(
  'supportReducer/closeTicketRequest',
  async (params = {}, thunkAPI) => {
    try {
      const response = await apiPatch(
        '/search/api/v1/ticket/:id/close'.replace(':id', params?.id),
        {}
      );

      return {
        data: response.data?.result?.data,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const replyTicketRequest = createAsyncThunk(
  'supportReducer/replyTicketRequest',
  async (params = {}, thunkAPI) => {
    try {
      const response = await apiPost(
        '/search/api/v1/ticket/:id/reply'.replace(':id', params?.id),
        params?.formData
      );

      return {
        data: response.data?.result?.data,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const addTicketRequest = createAsyncThunk(
  'supportReducer/addTicketRequest',
  async (params = {}, thunkAPI) => {
    try {
      const response = await apiPost('/search/api/v1/ticket', params?.formData);

      return {
        data: response.data?.result?.data,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const addSupportCategoryRequest = createAsyncThunk(
  'supportReducer/addSupportCategoryRequest',
  async (params = {}, thunkAPI) => {
    const { supportReducer } = thunkAPI.getState();
    try {
      const response = await apiPost('/search/api/v1/ticket-category', {
        ...params,
      });

      return {
        data: [...supportReducer.categories.data, response.data?.result?.data],
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const editSupportCategoryRequest = createAsyncThunk(
  'supportReducer/editSupportCategoryRequest',
  async (params = {}, thunkAPI) => {
    const { supportReducer } = thunkAPI.getState();
    const { id, ...restParams } = params;
    try {
      const response = await apiPost(
        '/search/api/v1/ticket-category/:id'.replace(':id', id),
        {
          ...restParams,
        }
      );

      let _temp = [...supportReducer.categories.data];
      _temp = _temp?.map((category) =>
        category.id === id
          ? {
              ...category,
              ...restParams,
            }
          : category
      );

      return {
        data: _temp,
        params: response.data?.result?.data,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const supportSlice = createSlice({
  name: 'supportSlice',
  initialState: supportInitialState,
  reducers: {
    setSelectedTicketCategory: (state, action) => {
      state.selectedCategory = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTickets.pending, (state) => {
        state.tickets.loading = true;
        state.tickets.error = null;
      })
      .addCase(fetchTickets.rejected, (state, action) => {
        state.tickets.loading = false;
        state.tickets.error = action.payload;
      })
      .addCase(fetchTickets.fulfilled, (state, action) => {
        const { data, total } = action.payload;
        state.tickets.data = data;
        state.tickets.total = total;
        state.tickets.loading = false;
        state.tickets.error = null;
      })
      .addCase(fetchTicket.pending, (state) => {
        state.ticket.loading = true;
        state.ticket.error = null;
      })
      .addCase(fetchTicket.rejected, (state, action) => {
        state.ticket.loading = false;
        state.ticket.error = action.payload;
      })
      .addCase(fetchTicket.fulfilled, (state, action) => {
        state.ticket.data = action.payload.data;
        state.ticket.loading = false;
        state.ticket.error = null;
      })
      .addCase(closeTicketRequest.pending, (state) => {
        state.closeTicket.loading = true;
        state.closeTicket.error = null;
      })
      .addCase(closeTicketRequest.rejected, (state, action) => {
        state.closeTicket.loading = false;
        state.closeTicket.error = action.payload;
      })
      .addCase(closeTicketRequest.fulfilled, (state, action) => {
        state.ticket.data = action.payload.data;
        state.closeTicket.loading = false;
        state.closeTicket.error = null;
      })
      .addCase(replyTicketRequest.pending, (state) => {
        state.replyTicket.loading = true;
        state.replyTicket.error = null;
      })
      .addCase(replyTicketRequest.rejected, (state, action) => {
        state.replyTicket.loading = false;
        state.replyTicket.error = action.payload;
      })
      .addCase(replyTicketRequest.fulfilled, (state, action) => {
        state.ticket.data = action.payload.data;
        state.replyTicket.loading = false;
        state.replyTicket.error = null;
      })
      .addCase(addTicketRequest.pending, (state) => {
        state.addTicket.loading = true;
        state.addTicket.error = null;
      })
      .addCase(addTicketRequest.rejected, (state, action) => {
        state.addTicket.loading = false;
        state.addTicket.error = action.payload;
      })
      .addCase(addTicketRequest.fulfilled, (state, action) => {
        state.ticket.data = action.payload.data;
        state.addTicket.loading = false;
        state.addTicket.error = null;
      })
      .addCase(fetchSupportCategories.pending, (state) => {
        state.categories.loading = true;
        state.categories.error = null;
      })
      .addCase(fetchSupportCategories.rejected, (state, action) => {
        state.categories.loading = false;
        state.categories.error = action.payload;
      })
      .addCase(fetchSupportCategories.fulfilled, (state, action) => {
        state.categories.data = action.payload.data;
        state.categories.loading = false;
        state.categories.error = null;
      })
      .addCase(deleteSupportCategories.pending, (state) => {
        state.removeCategories.loading = true;
        state.removeCategories.error = null;
      })
      .addCase(deleteSupportCategories.rejected, (state, action) => {
        state.removeCategories.loading = false;
        state.removeCategories.error = action.payload;
      })
      .addCase(deleteSupportCategories.fulfilled, (state, action) => {
        state.categories.data = action.payload.data;
        state.removeCategories.loading = false;
        state.removeCategories.error = null;
      })
      .addCase(addSupportCategoryRequest.pending, (state) => {
        state.addCategory.loading = true;
        state.addCategory.error = null;
      })
      .addCase(addSupportCategoryRequest.rejected, (state, action) => {
        state.addCategory.loading = false;
        state.addCategory.error = action.payload;
      })
      .addCase(addSupportCategoryRequest.fulfilled, (state, action) => {
        state.categories.data = action.payload.data;
        state.addCategory.loading = false;
        state.addCategory.error = null;
      })
      .addCase(editSupportCategoryRequest.pending, (state) => {
        state.editCategory.loading = true;
        state.editCategory.error = null;
      })
      .addCase(editSupportCategoryRequest.rejected, (state, action) => {
        state.editCategory.loading = false;
        state.editCategory.error = action.payload;
      })
      .addCase(editSupportCategoryRequest.fulfilled, (state, action) => {
        state.categories.data = action.payload.data;
        state.selectedCategory = action.payload.params;
        state.editCategory.loading = false;
        state.editCategory.error = null;
      });
  },
});

export const { setSelectedTicketCategory } = supportSlice.actions;
